import { useQueryClient } from "@tanstack/react-query";
import { FaPlusCircle, FaSearch } from "react-icons/fa";
import { Button as RBButton, Col, Form, Row } from "react-bootstrap";
import Select from 'react-select';
import { EmployeeConfigMasterFormModal, EmployeeConfigMasterTable } from "./components";
import axios, { AxiosError } from "axios";
import { useCompanyMaster } from "queries";
import { useMemo, useState } from "react";
import { CompanyMaster } from "models/companymaster/CompanyMaster";
import { useAddEmployeeConfigMaster, useDeleteEmployeeConfigMaster, useEmployeeConfigMaster } from "./queries";
import { useBoolean } from "usehooks-ts";
import { EmployeeConfigMaster } from "models/config/EmployeeConfigMaster";
import { extractAccessToken } from "helpers/jwt";
import Swal from "sweetalert2";
import { useUserInformation } from "hooks";

const EXCLUDED_SIDS = new Set(["1IbuASmpqIapjmhVfzcLF4XPVY9", "1JeqtQlfd6fuDpsFtUjp01mOR98"]);

const selectSmStyles = {
    control: (base) => ({ ...base, minHeight: '31px', height: '31px' }),
    valueContainer: (base) => ({ ...base, height: '31px', padding: '0 6px' }),
    indicatorsContainer: (base) => ({ ...base, height: '31px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
    clearIndicator: (base) => ({ ...base, padding: '4px' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const getErrorMessage = (error: unknown) =>
    axios.isAxiosError(error)
        ? (error as AxiosError<{ message?: string }>).response?.data.message
        : error instanceof Error
            ? error.message
            : 'Unknown error occurred.';

export const EmployeeRoles = () => {
    const queryClient = useQueryClient();

	const { companyOptions } = useUserInformation()
    const { data: companyMaster } = useCompanyMaster();

    const [pendingCompany, setPendingCompany] = useState<CompanyMaster>();
    const [selectedCompany, setSelectedCompany] = useState<CompanyMaster>();

    const handleSearch = () => {
        setSelectedCompany(pendingCompany);
    };

    const { data: employeeConfigMaster, isFetching: isFetchingEmployeeConfigMaster } = useEmployeeConfigMaster();
    const filteredEmployeeConfigMaster = useMemo(
        () => {
            const companyMap = new Map(companyMaster?.map(c => [c.sid, c]));
            return employeeConfigMaster
                ?.filter(employeeConfig => !EXCLUDED_SIDS.has(employeeConfig.sid))
                .filter(employeeConfig =>
                    selectedCompany && selectedCompany.sid !== 'all'
                        ? employeeConfig.sid === selectedCompany.sid
                        : true
                )
                .sort((a, b) => {
                    const idCompare = (a.id || "").localeCompare(b.id || "");
                    if (idCompare !== 0) return idCompare;
                    const aCode = companyMap.get(a.sid)?.company_code || "";
                    const bCode = companyMap.get(b.sid)?.company_code || "";
                    return aCode.localeCompare(bCode);
                });
        },
        [selectedCompany, employeeConfigMaster, companyMaster]
    );
    
    const { value: isOpenCreateModal, toggle: toggleIsOpenCreateModal } =
        useBoolean();
    const { mutate: addEmployeeConfigMaster } = useAddEmployeeConfigMaster();

    const { mutate: deleteEmployeeConfigMaster } = useDeleteEmployeeConfigMaster();

    const handleSubmitAddEmployeeConfigMaster = (employeeConfigMasters: EmployeeConfigMaster) => {
        const request = {
            ...employeeConfigMasters,
            created_by:
                extractAccessToken().citizen_id || extractAccessToken().username
        }

        addEmployeeConfigMaster(request, {
            onSuccess: async () => {
                await Swal.fire({
                    title: 'Success!',
                    icon: 'success'
                });
            },
            onError: async error => {
                await Swal.fire({
                    title: 'Error!',
                    icon: 'error',
                    text: getErrorMessage(error),
                });
            },
            onSettled: () => {
                toggleIsOpenCreateModal();

                queryClient.invalidateQueries({
                    queryKey: ['employee-config-master']
                });
            }
        });
    };

    const handleDeleteEmployeeConfigMaster = async (employeeConfigMaster: EmployeeConfigMaster) => {
        const result = await Swal.fire({
            title: 'ลบข้อมูล ' + employeeConfigMaster?.id + ' ?',
            text: 'คุณต้องการลบหรือไม่ ?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: '✔ ใช่',
            cancelButtonText: '✖ ไม่',
            allowOutsideClick: false
        });
        if (!result.isConfirmed) {
            return;
        }
        
        deleteEmployeeConfigMaster(employeeConfigMaster, {
            onSuccess: async () => {
                await Swal.fire({
                    title: 'Success!',
                    icon: 'success'
                });
            },
            onError: async error => {
                await Swal.fire({
                    title: 'Error!',
                    icon: 'error',
                    text: getErrorMessage(error),
                });
            },
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['employee-config-master']
                });
            }
        });
    };

    return (
        <div className="master-data-content">
            <div>
                <div className="trapezoid-head tw-w-[200px]" style={{ display: 'inline-block' }}>
                    <span>Employee Roles</span>
                </div>
                <div style={{ display: 'inline', float: 'right' }}>
                    <RBButton variant="info" size="sm" style={{ float: 'right', marginLeft: '5px' }} onClick={toggleIsOpenCreateModal}>
                        <FaPlusCircle style={{ marginBottom: '4px' }} /> Add
                    </RBButton>
                </div>
            </div>

            <div className="mat-box shadow p-3 mb-5 bg-white rounded">
                <div className='trapezoid trapezoid-purple' style={{ width: '200px', display: 'inline-block' }}>
                    <span>Search menu</span>
                </div>
                <div className="mat-box shadow p-3 mb-5 bg-white rounded">
                    <Row>
                        <Col sm={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Company :</Form.Label>
                                <Select
                                    menuPortalTarget={document.body}
                                    styles={selectSmStyles}
                                    options={companyOptions}
                                    value={pendingCompany}
                                    getOptionLabel={(company: CompanyMaster) => company.company_name}
                                    getOptionValue={(company: CompanyMaster) => company.sid}
                                    onChange={setPendingCompany}
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col lg={{ span: 2, offset: 6 }}>
                            <div className="mb-3 w-100 mt-4 d-flex justify-content-end">
                                <RBButton variant="info" size="sm" className="mx-1 btn btn-block" onClick={handleSearch}>
                                    <FaSearch /> Search
                                </RBButton>
                            </div>
                        </Col>
                    </Row>
                </div>

                <EmployeeConfigMasterTable
                    isPending={isFetchingEmployeeConfigMaster}
                    employeeConfigs={filteredEmployeeConfigMaster}
                    onDelete={handleDeleteEmployeeConfigMaster}
                />

                {/* Create modal */}
                <EmployeeConfigMasterFormModal
                    title="Create Employee Config"
                    isOpen={isOpenCreateModal}
                    onClose={toggleIsOpenCreateModal}
                    onSubmit={handleSubmitAddEmployeeConfigMaster}
                />
            </div>
        </div>
    )
};